import type { DepartureDto, ExperienceDto, PackageDto } from "@tripcart/shared";
import { applySeasonToPackage } from "./_season-pricing";

type SeasonDto = NonNullable<ExperienceDto["seasons"]>[number];

/** One tier band, flattened for the calendar's group-discount tooltip. */
export interface CalendarPriceTier {
  from: number;
  to: number | null;
  priceMinor: number;
}

/** The price the calendar renders under one date.
 *
 *  `amountMinor === 0` means the tour is genuinely FREE on that date — a
 *  supported product (see `_booking-config.ts`: "a zero price is a price").
 *  A date with no price at all is ABSENT from the map instead, which is why
 *  the two are distinguishable here and are not on `DepartureDto.priceFrom`
 *  alone (the API substitutes `{amount: 0}` for an unpriced experience). */
export interface CalendarDayPrice {
  amountMinor: number;
  /** True when this date is among the CHEAPEST in the loaded window — the
   *  signal a traveler scanning for a bargain is actually looking for, and
   *  the pattern Skyscanner and GetYourGuide both use.
   *
   *  Comparative, not absolute: a date is only marked when the window
   *  actually varies in price. On a flat-priced tour every date would
   *  otherwise light up, which highlights nothing.
   *
   *  Never the ONLY signal — the cell also renders it in a heavier weight, so
   *  it survives colour blindness (WCAG 1.4.1). */
  isLowestPrice: boolean;
  /** The package the price came from. Named in the accessible label so a
   *  traveler on a multi-package tour can tell WHICH option costs this. */
  packageName: string;
  /** Bands for the group-discount tooltip. Absent when the winning package's
   *  default category is flat on this date. */
  tiers?: readonly CalendarPriceTier[];
}

/** The default price category, ignoring whole-group fees.
 *
 *  A `per_group` category is ONE fee for the entire party, not a price per
 *  traveler. Quoting it under a date reads as "this day costs ₨9,000 each"
 *  when ₨9,000 actually covers everyone — the single most alarming thing the
 *  cell could say, and wrong in the direction that loses bookings.
 *
 *  Otherwise this mirrors the sidebar's own resolution (operator-flagged
 *  default, else the first category) so the two agree by construction. The one
 *  place they can now differ is a package whose flagged default IS the group
 *  fee: the sidebar still headlines it, and the calendar prices the package
 *  from its per-traveler categories instead — or contributes no price at all
 *  if it has none. */
function defaultCategoryOf(pkg: PackageDto) {
  const perTraveler = pkg.categories.filter((c) => c.chargeBase !== "group");
  return perTraveler.find((c) => c.isDefault) ?? perTraveler[0];
}

/** One traveler's price for this category, or `null` when there isn't one.
 *
 *  Deliberately NOT `resolveCategoryUnitPriceMinor`, which ends in
 *  `cat.flat?.now.amount ?? 0`. A tiered category carries no `flat` price, so
 *  if the operator's bands start at 2+ that resolver returns 0 — and this
 *  calendar renders 0 as "Free". A tour with an unreachable first band would
 *  advertise itself as free on every date.
 *
 *  `null` (nothing to show) and `0` (genuinely free) stay distinguishable. */
function unitPriceAtOnePax(
  cat: PackageDto["categories"][number],
): number | null {
  if (cat.strategy === "tiered" && cat.tiers?.length) {
    const band = cat.tiers.find(
      (t) => 1 >= t.from && (t.to === null || 1 <= t.to),
    );
    if (band) return band.price.amount;
  }
  return cat.flat?.now.amount ?? null;
}

/** Price one package on one season. Deliberately the SAME derivation the
 *  sidebar headline uses — `applySeasonToPackage` then the default category at
 *  one pax — so the calendar cell and the widget can never quote different
 *  numbers for the same package.
 *
 *  With no season this equals the server's `pkg.headlinePrice`; a test pins
 *  that, so a change to the server's anchor math fails loudly rather than
 *  drifting. */
function priceForPackage(
  pkg: PackageDto,
  season: SeasonDto | null,
): CalendarDayPrice | null {
  const seasoned = applySeasonToPackage(pkg, season);
  const cat = defaultCategoryOf(seasoned);
  if (!cat) return null;

  const amountMinor = unitPriceAtOnePax(cat);
  if (amountMinor === null) return null;

  const tiers =
    cat.strategy === "tiered" && cat.tiers?.length
      ? cat.tiers.map((t) => ({
          from: t.from,
          to: t.to,
          priceMinor: t.price.amount,
        }))
      : undefined;

  return {
    amountMinor,
    // Filled in by `markLowest` once the whole window is known — a single
    // date cannot tell whether it is the cheapest one.
    isLowestPrice: false,
    packageName: pkg.title,
    ...(tiers ? { tiers } : {}),
  };
}

/** Flag every date sitting at the window's minimum price.
 *
 *  Deliberately a second pass: "cheapest" is a property of the window, not of
 *  a date. Skipped entirely when every date costs the same — marking all of
 *  them would draw the eye to nothing. */
function markLowest(prices: Map<string, CalendarDayPrice>): void {
  let min = Infinity;
  let max = -Infinity;
  for (const p of prices.values()) {
    if (p.amountMinor < min) min = p.amountMinor;
    if (p.amountMinor > max) max = p.amountMinor;
  }
  if (min === max) return;
  for (const p of prices.values()) {
    if (p.amountMinor === min) p.isLowestPrice = true;
  }
}

/** Per-date view of the availability window: which season the date falls in
 *  and which packages actually run on it. */
interface DateFacts {
  seasonId: string | undefined;
  packageIds: Set<string>;
  /** True when at least one departure carried no `packageId` — the API did
   *  not fan out per package on this date, so it is not telling us which
   *  package runs and every configured package has to be considered. */
  allPackages: boolean;
}

function collectDateFacts(departures: DepartureDto[]): Map<string, DateFacts> {
  const byDate = new Map<string, DateFacts>();
  for (const d of departures) {
    let facts = byDate.get(d.date);
    if (!facts) {
      // Every departure on a date shares one season — the API resolves the
      // season per DATE, before fanning out over packages and slots — so the
      // first row's `seasonId` is the date's season, not one of several.
      facts = {
        seasonId: d.seasonId,
        packageIds: new Set(),
        allPackages: false,
      };
      byDate.set(d.date, facts);
    }
    if (d.packageId) facts.packageIds.add(d.packageId);
    else facts.allPackages = true;
  }
  return byDate;
}

/** Last-resort map built straight from the wire when no package data reached
 *  the calendar (demo fixtures, the params-driven `sites` route).
 *
 *  `DepartureDto.priceFrom` is the package's default category, which need
 *  not be what the resolver above would show — acceptable only because the
 *  alternative here is no price at all. Zero is dropped rather than shown: on this path a genuinely free tour
 *  and an unpriced one are indistinguishable, and legacy's rule was to hide
 *  (`shouldDisplayDate`: `selling <= 0` → false). */
function fallbackPrices(
  departures: DepartureDto[],
): Map<string, CalendarDayPrice> {
  const map = new Map<string, CalendarDayPrice>();
  for (const d of departures) {
    if (d.priceFrom.amount <= 0) continue;
    const existing = map.get(d.date);
    if (existing && existing.amountMinor <= d.priceFrom.amount) continue;
    map.set(d.date, {
      amountMinor: d.priceFrom.amount,
      isLowestPrice: false,
      packageName: d.packageName ?? "",
    });
  }
  markLowest(map);
  return map;
}

/** Resolves and memoises `(package, season)` prices. An experience has a
 *  handful of each, so a 60-day window costs a handful of resolutions rather
 *  than one per date. */
function makePriceResolver(
  packages: PackageDto[],
  seasons: SeasonDto[] | undefined,
): (
  packageId: string,
  seasonId: string | undefined,
) => CalendarDayPrice | null {
  const seasonById = new Map((seasons ?? []).map((s) => [s.id, s]));
  const packageById = new Map(packages.map((p) => [p.id, p]));
  const cache = new Map<string, CalendarDayPrice | null>();

  return (packageId, seasonId) => {
    const key = `${packageId}:${seasonId ?? ""}`;
    const hit = cache.get(key);
    if (hit !== undefined) return hit;
    const pkg = packageById.get(packageId);
    const season = (seasonId && seasonById.get(seasonId)) || null;
    const resolved = pkg ? priceForPackage(pkg, season) : null;
    cache.set(key, resolved);
    return resolved;
  };
}

/** The cheapest of the packages running on one date. */
function cheapestOnDate(
  facts: DateFacts,
  allPackageIds: string[],
  priceFor: (
    packageId: string,
    seasonId: string | undefined,
  ) => CalendarDayPrice | null,
): CalendarDayPrice | null {
  const candidates = facts.allPackages ? allPackageIds : [...facts.packageIds];
  let best: CalendarDayPrice | null = null;
  for (const id of candidates) {
    const price = priceFor(id, facts.seasonId);
    if (price && (!best || price.amountMinor < best.amountMinor)) best = price;
  }
  // Copy: the resolver caches one object per `(package, season)` pair, so many
  // dates would otherwise alias a single record and `markLowest` would write
  // through all of them at once.
  return best ? { ...best } : null;
}

export interface ResolveCalendarPricesInput {
  departures: DepartureDto[] | null;
  packages?: PackageDto[];
  seasons?: SeasonDto[];
}

/**
 * Price every date in the loaded availability window.
 *
 * The number is the LOWEST default-category price, at one pax, across the
 * packages that actually run that day, with the date's season applied.
 *
 * Why the lowest across PACKAGES but always the DEFAULT category:
 *   - Lowest across packages is honest — it is a real price a solo adult can
 *     book that day, and it answers the question the calendar exists to answer
 *     ("which day should I go?").
 *   - Lowest across CATEGORIES would resolve to the Child price, which nobody
 *     can book alone. The widget seeds one adult, so the traveler would see a
 *     price and then be quoted a much higher one having done nothing.
 *
 * It also stays stable: nothing here reads the traveler picker or the selected
 * package, so the numbers move only when the DATE moves.
 */
export function resolveCalendarPrices({
  departures,
  packages,
  seasons,
}: ResolveCalendarPricesInput): Map<string, CalendarDayPrice> {
  if (!departures?.length) return new Map();
  if (!packages?.length) return fallbackPrices(departures);

  const priceFor = makePriceResolver(packages, seasons);
  const allPackageIds = packages.map((p) => p.id);

  const result = new Map<string, CalendarDayPrice>();
  for (const [date, facts] of collectDateFacts(departures)) {
    const best = cheapestOnDate(facts, allPackageIds, priceFor);
    if (best) result.set(date, best);
  }
  markLowest(result);
  return result;
}
